import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { configDeviceCamera } from 'src/features/deviceSlice';
import {
  CAMERA_CONFIG_KEYS,
  buildInitialCameraConfig,
  hasKeepLiveValue,
  isForceLiveValueValid,
  isNonNegativeInteger
} from '../utils';

// Cấu hình camera:
// - 361 (live 4G): gạt switch -> ModalConfirm -> gọi API riêng ngay (onLive4gChanged khi thành công)
// - 362 gạt tắt: ModalConfirm -> gọi API ngay với 0
// - 362 (giữ live sau ACC), 360 (xem cưỡng bức, one-shot): gửi qua nút "Lưu lại" — xem applyCameraConfig
export default function useCameraConfig(
  d,
  { onLive4gChanged, onKeepLiveOff } = {}
) {
  const dispatch = useDispatch();

  const [cameraConfig, setCameraConfig] = useState(() =>
    buildInitialCameraConfig(d)
  );
  const [savedCameraConfig, setSavedCameraConfig] = useState(() =>
    buildInitialCameraConfig(d)
  );
  const [cameraSubmitting, setCameraSubmitting] = useState(false);

  const [live4gConfirmOpen, setLive4gConfirmOpen] = useState(false);
  const [nextLive4g, setNextLive4g] = useState(null);
  const [live4gSubmitting, setLive4gSubmitting] = useState(false);

  const [keepLiveOffConfirmOpen, setKeepLiveOffConfirmOpen] = useState(false);
  const [keepLiveOffSubmitting, setKeepLiveOffSubmitting] = useState(false);

  useEffect(() => {
    setCameraConfig(buildInitialCameraConfig(d));
    setSavedCameraConfig(buildInitialCameraConfig(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.id]);

  const updateCameraConfig = (field, value) =>
    setCameraConfig(prev => ({ ...prev, [field]: value }));

  // 362 / 360 chỉ cho nhập khi Live & Playback qua 4G đang bật và switch của ô đang bật
  const isLive4gOn = cameraConfig.live4g === 1;
  const isKeepLiveActive = isLive4gOn && cameraConfig.keepLiveEnabled;
  const isForceLiveActive = isLive4gOn && cameraConfig.forceLiveEnabled;

  const isKeepLiveInvalid =
    isKeepLiveActive && !isNonNegativeInteger(cameraConfig.keepLive);
  const isForceLiveInvalid =
    isForceLiveActive && !isForceLiveValueValid(cameraConfig.forceLive);

  const cameraConfigPayload = useMemo(() => {
    const payload = {};

    // 362: chỉ gửi khi khác giá trị đã lưu (gạt tắt thì gọi API riêng — xem onConfirmKeepLiveOff)
    const savedKeepLive = savedCameraConfig.keepLiveEnabled
      ? Number(savedCameraConfig.keepLive)
      : 0;
    if (
      isKeepLiveActive &&
      !isKeepLiveInvalid &&
      Number(cameraConfig.keepLive) !== savedKeepLive
    ) {
      payload[CAMERA_CONFIG_KEYS.keepLive] = Number(cameraConfig.keepLive);
    }
    if (isForceLiveActive && !isForceLiveInvalid) {
      payload[CAMERA_CONFIG_KEYS.forceLive] = Number(cameraConfig.forceLive);
    }

    return payload;
  }, [
    cameraConfig,
    savedCameraConfig,
    isKeepLiveActive,
    isKeepLiveInvalid,
    isForceLiveActive,
    isForceLiveInvalid
  ]);

  const hasCameraConfigChanges = Object.keys(cameraConfigPayload).length > 0;

  const hasCameraConfigErrors = isKeepLiveInvalid || isForceLiveInvalid;

  // ---- 361: bật/tắt Live & Playback qua 4G (có xác nhận, gọi API ngay) ----
  const onToggleLive4g = e => {
    if (live4gSubmitting || cameraSubmitting) return;
    setNextLive4g(e.target.checked ? 1 : 0);
    setLive4gConfirmOpen(true);
  };

  const onConfirmLive4g = async () => {
    try {
      setLive4gSubmitting(true);
      await dispatch(
        configDeviceCamera({
          device_id: d.id,
          data: { [CAMERA_CONFIG_KEYS.live4g]: nextLive4g }
        })
      ).unwrap();

      // Thành công mới đổi trạng thái hiển thị; thất bại giữ nguyên (thunk đã báo lỗi)
      setCameraConfig(prev => ({ ...prev, live4g: nextLive4g }));
      setSavedCameraConfig(prev => ({ ...prev, live4g: nextLive4g }));
      if (onLive4gChanged) onLive4gChanged(nextLive4g);
    } catch (err) {
      console.error(err);
    } finally {
      setLive4gSubmitting(false);
      setLive4gConfirmOpen(false);
    }
  };

  // ---- 362: gạt tắt "Giữ live sau khi tắt ACC" (có xác nhận, gọi API ngay với 0) ----
  const onToggleKeepLive = e => {
    if (keepLiveOffSubmitting || cameraSubmitting) return;
    // Bật, hoặc tắt khi chưa lưu bật trên thiết bị: chỉ đổi ở form
    if (e.target.checked || !savedCameraConfig.keepLiveEnabled) {
      updateCameraConfig('keepLiveEnabled', e.target.checked);
      return;
    }
    setKeepLiveOffConfirmOpen(true);
  };

  const onConfirmKeepLiveOff = async () => {
    try {
      setKeepLiveOffSubmitting(true);
      await dispatch(
        configDeviceCamera({
          device_id: d.id,
          data: { [CAMERA_CONFIG_KEYS.keepLive]: 0 }
        })
      ).unwrap();

      const offConfig = { keepLive: '0', keepLiveEnabled: false };
      setCameraConfig(prev => ({ ...prev, ...offConfig }));
      setSavedCameraConfig(prev => ({ ...prev, ...offConfig }));
      if (onKeepLiveOff) onKeepLiveOff();
    } catch (err) {
      console.error(err);
    } finally {
      setKeepLiveOffSubmitting(false);
      setKeepLiveOffConfirmOpen(false);
    }
  };

  const applyCameraConfig = async () => {
    if (cameraSubmitting || hasCameraConfigErrors) return { ok: false };
    if (!hasCameraConfigChanges) return { ok: true, appliedConfig: null };

    try {
      setCameraSubmitting(true);
      await dispatch(
        configDeviceCamera({ device_id: d.id, data: cameraConfigPayload })
      ).unwrap();
    } catch (err) {
      console.error(err);
      return { ok: false };
    } finally {
      setCameraSubmitting(false);
    }

    // Gửi xong: switch 362 phản ánh giá trị vừa gửi (0 = tắt);
    // tắt switch 360 (lệnh one-shot), giữ nguyên giá trị đã nhập để hiển thị
    const keepLiveSent = cameraConfigPayload[CAMERA_CONFIG_KEYS.keepLive];
    const appliedConfig = {
      ...cameraConfig,
      ...(keepLiveSent === 0 && { keepLive: '0' }),
      keepLiveEnabled:
        isLive4gOn && hasKeepLiveValue(keepLiveSent ?? cameraConfig.keepLive)
          ? cameraConfig.keepLiveEnabled
          : false,
      forceLiveEnabled: false
    };
    setCameraConfig(appliedConfig);
    setSavedCameraConfig(appliedConfig);
    return { ok: true, appliedConfig };
  };

  return {
    cameraConfig,
    savedCameraConfig,
    cameraSubmitting,
    isLive4gOn,
    isKeepLiveActive,
    isForceLiveActive,
    isKeepLiveInvalid,
    isForceLiveInvalid,
    hasCameraConfigErrors,
    updateCameraConfig,
    applyCameraConfig,
    live4gSubmitting,
    onToggleLive4g,
    live4gConfirmProps: {
      title: 'Chú ý',
      content:
        nextLive4g === 1
          ? 'Bạn có chắc chắn muốn bật Live & Playback qua 4G không?'
          : 'Bạn có chắc chắn muốn tắt Live & Playback qua 4G không?',
      textOk: 'Đồng ý',
      textCancel: 'Hủy',
      open: live4gConfirmOpen,
      loading: live4gSubmitting,
      setOpen: value => {
        if (!live4gSubmitting) setLive4gConfirmOpen(value);
      },
      callbackOk: onConfirmLive4g
    },
    keepLiveOffSubmitting,
    onToggleKeepLive,
    keepLiveOffConfirmProps: {
      title: 'Chú ý',
      content: 'Bạn có chắc chắn muốn tắt giữ live sau khi tắt ACC không?',
      textOk: 'Đồng ý',
      textCancel: 'Hủy',
      open: keepLiveOffConfirmOpen,
      loading: keepLiveOffSubmitting,
      setOpen: value => {
        if (!keepLiveOffSubmitting) setKeepLiveOffConfirmOpen(value);
      },
      callbackOk: onConfirmKeepLiveOff
    }
  };
}
